import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface SelectedAddress {
  formattedAddress: string;
  zipCode: string;
  city?: string;
  lat: number;
  lng: number;
}

interface Props {
  onSelect: (a: SelectedAddress) => void;
  placeholder?: string;
  initialValue?: string;
  onManualChange?: (value: string) => void;
  onAvailabilityChange?: (available: boolean) => void;
}

/**
 * Google Places (New) Autocomplete restricted to Spain via Lovable Cloud.
 * Browser Places can be blocked by Google key restrictions, so suggestions
 * and place details are proxied through the connected Google Maps connector.
 */
export const AddressAutocomplete = ({ onSelect, placeholder, initialValue = "", onManualChange, onAvailabilityChange }: Props) => {
  const [input, setInput] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<Array<{ id: string; text: string }>>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready] = useState(true);
  const sessionTokenRef = useRef<string>(crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
  const debounceRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = async (value: string) => {
    if (!value || value.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const { data: sess } = await supabase.auth.getSession();
    if (!sess.session) {
      // Address suggestions are only for signed-in users; guests type it by hand.
      onAvailabilityChange?.(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-places", {
        body: {
          action: "autocomplete",
          input: value,
          sessionToken: sessionTokenRef.current,
        },
      });
      if (error) throw error;
      const mapped = (data?.suggestions ?? []) as Array<{ id: string; text: string }>;
      onAvailabilityChange?.(!data?.googleError);
      setSuggestions(mapped);
      setOpen(mapped.length > 0);
    } catch (e) {
      console.error("[AddressAutocomplete] fetch failed", e);
      onAvailabilityChange?.(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (v: string) => {
    setInput(v);
    onManualChange?.(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchSuggestions(v), 250);
  };

  const handlePick = async (suggestion: { id: string; text: string }) => {
    setOpen(false);
    setInput(suggestion.text);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-places", {
        body: {
          action: "details",
          placeId: suggestion.id,
          sessionToken: sessionTokenRef.current,
        },
      });
      if (error) throw error;
      const formatted = data?.formattedAddress ?? suggestion.text;
      setInput(formatted);
      sessionTokenRef.current = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      // Fallback: Google sometimes omits the postal_code component even when
      // the formatted address contains a Spanish ZIP (5 digits). Extract it
      // from the text so validation doesn't block the user.
      let zip = (data?.zipCode ?? "").trim();
      if (!/^\d{5}$/.test(zip)) {
        const match = formatted.match(/\b(\d{5})\b/);
        if (match) zip = match[1];
      }
      onSelect({
        formattedAddress: formatted,
        zipCode: zip,
        city: (data?.city ?? "").trim() || undefined,
        lat: Number(data?.lat),
        lng: Number(data?.lng),
      });
    } catch (e) {
      console.error("[AddressAutocomplete] pick failed", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
          strokeWidth={1.5}
        />
        <Input
          value={input}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder ?? "Calle, número, ciudad…"}
          autoComplete="off"
          className="pl-9 pr-9"
          disabled={!ready}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg overflow-hidden">
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => handlePick(s)}
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent flex items-start gap-2"
              >
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <span>{s.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
